import { untouch } from 'redux-form';

import store from 'app/store/index';
import { dispatchThunk } from 'app/store';
import { FormsConfigActions } from 'app/store/data/formsConfig';
import { setQuestionRequirability } from 'app/store/data/formsConfig/thunks';
import {
  changeFieldValueAndUpdate,
  clearFieldValue,
  getCarData,
  getCompleteDataFormValues,
  getQuestion,
  getQuotation,
  setMessageVisibility,
  setQuestionAvailability,
} from 'app/services/businessFunctionHelpers';
import {
  FieldsEnum,
  FormsEnum,
  LabelEnum,
  MessagesEnum,
} from 'app/shared/enums';
import { isValidCarRegistrationNumber } from 'app/validators/validateCarRegistrationNumber';
import { setupInsuranceHistoryVisibility } from 'app/services/businessFunctions/helpers/setupInsuranceHistoryVisibility';
import { setPersonalDataMessage } from 'app/services/businessFunctions/helpers/setPersonalDataMessage';
import { getIsMubiLeasingConfig } from 'app/shared/helpers/getIsMubiLeasingConfig';
import { historyQuestionsVisibilityBasedOnPersonalData } from 'app/services/env.service';
import { checkIsDataObfuscated } from 'app/services/businessFunctions/helpers/checkIsDataObfuscated';

const carRegistrationNumber = {
  carRegistrationNumberChange(initialRun?: boolean) {
    const { registrationNumberNotKnown, registrationNumber } = getCarData();
    const { id: quotationId, car } = getQuotation();
    const question = getQuestion(FieldsEnum.CarRegistrationNumber);

    const isAnonymized = (registrationNumber || '').includes('***');
    const isValid =
      isValidCarRegistrationNumber(registrationNumber) ||
      (!question.required && registrationNumber === '');
    const isMubiLeasingConfig = getIsMubiLeasingConfig();

    setMessageVisibility({
      key: MessagesEnum.QuotationRegistrationNumberObfuscated,
      visible: isAnonymized,
      formName: FormsEnum.CalculationForm,
    });

    if (!isMubiLeasingConfig && historyQuestionsVisibilityBasedOnPersonalData) {
      setupInsuranceHistoryVisibility();
    }

    if (isMubiLeasingConfig) {
      dispatchThunk(
        setQuestionRequirability({
          formName: FormsEnum.CalculationForm,
          key: FieldsEnum.CarVin,
          required: registrationNumberNotKnown || !registrationNumber,
          optionalHelperText: LabelEnum.FieldNotRequired,
        })
      );
    }

    setPersonalDataMessage();

    if (initialRun) {
      return;
    }

    if (isValid) {
      if (question.shouldPatch) {
        changeFieldValueAndUpdate(
          FieldsEnum.CarRegistrationNumber,
          registrationNumber
        );
      }

      if (!store.getState().formsConfig.previousRegistrationNumber) {
        store.dispatch(
          FormsConfigActions.setPreviousRegistrationNumber({
            [quotationId]: car?.registrationNumber || undefined,
          })
        );
      }
    }
  },

  registrationNumberNotKnownChange(initialRun?: boolean) {
    const { registrationNumberNotKnown, registrationNumber } = getCarData();
    let registrationNumberStatus = registrationNumberNotKnown;
    const isMubiLeasingConfig = getIsMubiLeasingConfig();

    if (initialRun && !!registrationNumber && registrationNumberNotKnown) {
      registrationNumberStatus = false;

      changeFieldValueAndUpdate(
        FieldsEnum.CarRegistrationNumberNotKnown,
        registrationNumberStatus
      );
    } else if (registrationNumberNotKnown && !!registrationNumber) {
      clearFieldValue(FieldsEnum.CarRegistrationNumber);
    }

    if (isMubiLeasingConfig) {
      dispatchThunk(
        setQuestionRequirability({
          formName: FormsEnum.CalculationForm,
          key: FieldsEnum.CarVin,
          required: registrationNumberNotKnown || !registrationNumber,
          optionalHelperText: LabelEnum.FieldNotRequired,
        })
      );
    }

    setTimeout(
      () =>
        setQuestionAvailability({
          key: FieldsEnum.CarRegistrationNumber,
          enabled: !registrationNumberStatus,
          formName: FormsEnum.CalculationForm,
        }),
      0
    );

    setMessageVisibility({
      key: MessagesEnum.RegistrationNumberNotKnown,
      visible: !!registrationNumberStatus,
      formName: FormsEnum.CalculationForm,
    });

    const showObfuscatedMessage = checkIsDataObfuscated(
      registrationNumber,
      initialRun
    );

    setMessageVisibility({
      key: MessagesEnum.QuotationRegistrationNumberObfuscated,
      visible: showObfuscatedMessage && registrationNumberNotKnown === false,
      formName: FormsEnum.CalculationForm,
    });

    setMessageVisibility({
      key: MessagesEnum.CarRegistrationNumberError,
      visible: false,
      formName: FormsEnum.CalculationForm,
    });

    setMessageVisibility({
      key: MessagesEnum.CarRegistrationNumberInvalid,
      visible: false,
      formName: FormsEnum.CalculationForm,
    });

    if (historyQuestionsVisibilityBasedOnPersonalData && !isMubiLeasingConfig) {
      setupInsuranceHistoryVisibility();
    }

    if (registrationNumberNotKnown) {
      setPersonalDataMessage();
    }
  },

  onCompleteDataCarRegistrationNumberNotKnownChange() {
    const { car } = getCompleteDataFormValues();
    let registrationNumberStatus = Boolean(car?.registrationNumberNotKnown);

    if (car?.registrationNumberNotKnown && Boolean(car?.registrationNumber)) {
      clearFieldValue(
        FieldsEnum.CompleteDataCarRegistrationNumber,
        FormsEnum.CompleteDataForm,
        true
      );
    }

    if (registrationNumberStatus) {
      store.dispatch(
        untouch(
          FormsEnum.CompleteDataForm,
          FieldsEnum.CompleteDataCarRegistrationNumber
        )
      );
    }

    setTimeout(
      () =>
        setQuestionAvailability({
          key: FieldsEnum.CompleteDataCarRegistrationNumber,
          enabled: !registrationNumberStatus,
          formName: FormsEnum.CompleteDataForm,
        }),
      0
    );

    setMessageVisibility({
      key: MessagesEnum.RegistrationNumberNotKnown,
      visible: !!registrationNumberStatus,
      formName: FormsEnum.CompleteDataForm,
    });
  },
};

export default carRegistrationNumber;
